import * as ngCore from '@angular/core'
import * as ngRouter from '@angular/router'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as router from '@attain-apps/fe/web/main/lib/router'

import * as state from '../../../../../../../state'
import * as modals from '../../modals'
import * as shared from '../shared'

import * as assignedRecords from './assigned-records'
import * as functionalUsage from './functional-usage'
import * as general from './general'

/** Component for displaying Space cluster details. */
@ngCore.Component({
  imports: [
    ds.layout.page.Component,
    ds.layout.page.child.header.Component,
    general.Component,
    functionalUsage.Component,
    assignedRecords.Component,
    shared.occupants.Component,
    shared.responsiblePersons.Component,
    modals.cluster.Component,
  ],
  selector: 'atn-space-cluster-details',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link router.Router} */
  private readonly _router = ngCore.inject(router.Router)
  /** Activated route used to pass relative path in breadcrumb */
  private readonly _activatedRoute = ngCore.inject(ngRouter.ActivatedRoute)

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** @see {@link state.space.organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(
    state.space.organizations.Feature,
  )

  /** @see {@link state.space.full.clusters.Collection} */
  public readonly clustersEc = ngCore.inject(
    state.space.full.clusters.Collection,
  )

  /** @see {@link state.space.full.clusterOccupants.Collection} */
  public readonly occupantsEc = ngCore.inject(
    state.space.full.clusterOccupants.Collection,
  )

  /** @see {@link state.space.full.responsiblePersons.clusterResponsiblePersons.Collection} */
  public readonly clusterResponsiblePersonsEc = ngCore.inject(
    state.space.full.responsiblePersons.clusterResponsiblePersons.Collection,
  )

  /** @see {@link state.space.full.warnings.plugin.Plugin} */
  public readonly clusterWarningsPlugin = this.clustersEc.plugins
    .lookup(state.space.full.warnings.plugin.Plugin)
    .get()

  /** Current cluster */
  private readonly _clusterSg = ngCore.computed(() =>
    this.clustersEc.plugins.currentEntityPlugin.feature.selectors
      .getEntity()
      .asSignal(),
  )

  /** Current cluster name */
  public readonly clusterNameSg = ngCore.computed(
    (cluster = this._clusterSg()) => cluster?.name,
  )

  /** Breadcrumbs, only the first element in array should be clickable */
  public readonly breadcrumbsSg = ngCore.computed<
    ds.layout.page.child.header.models.Breadcrumb[]
  >(
    (
      cluster = this._clusterSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) => [
      //
      {
        name: 'Clusters',
        url: this._router.ngRouter.createUrlTree(
          [`../${ents.extra.prebuilt.Page.Id.SpaceClusters}`],
          {
            queryParamsHandling: 'preserve',
            relativeTo: this._activatedRoute,
          },
        ),
      },
      // Cluster organizations, if any
      ...((cluster &&
        schema
          .getEntitiesInDimension(
            state.space.organizations.constants.DIMENSION_ID,
          )
          .filter(organization => !!cluster.organization[organization.id])
          .map(organization => {
            const organizationName = this._organizationsFeature.selectors
              .getOrganizationEc(organization.id)
              .asSignal()
              ?.plugins.cachePlugin.feature.selectors.getCachedEntity(
                cluster.organization[organization.id],
              )
              .asSignal()?.name

            return { name: organizationName ?? organization.name }
          })) ??
        []),
      {
        name: cluster?.name ?? 'Cluster',
      },
    ],
  )
}
